import React from 'react';
import { Box, Typography, Button, Paper, Grid } from '@mui/material';
import type { TrainingPlan, TrainingFormData } from '../types';
import './printStyles.css';

interface TrainingPlanDisplayProps {
  plan: TrainingPlan;
  formData: TrainingFormData;
  onReset: () => void;
}

const getWorkoutTypeStyles = (type: string) => {
  const baseStyles = {
    p: 1,
    borderRadius: '16px',
    border: 1,
    display: 'inline-block',
    fontSize: '0.75rem',
    fontWeight: 'bold',
  };
  switch (type.toLowerCase()) {
    case 'long run':
      return { ...baseStyles, bgcolor: 'primary.light', color: 'primary.contrastText', borderColor: 'primary.main' };
    case 'easy run':
    case 'recovery run':
      return { ...baseStyles, bgcolor: 'success.light', color: 'success.contrastText', borderColor: 'success.main' };
    case 'intervals':
    case 'interval training':
      return { ...baseStyles, bgcolor: 'error.light', color: 'error.contrastText', borderColor: 'error.main' };
    case 'tempo run':
      return { ...baseStyles, bgcolor: 'warning.dark', color: 'white', borderColor: 'warning.main' };
    case 'rest':
      return { ...baseStyles, bgcolor: 'grey.700', color: 'white', borderColor: 'grey.500' };
    case 'cross-training':
      return { ...baseStyles, bgcolor: 'secondary.light', color: 'secondary.contrastText', borderColor: 'secondary.main' };
    default:
      return { ...baseStyles, bgcolor: 'warning.light', color: 'warning.contrastText', borderColor: 'warning.main' };
  }
};

const formatDate = (dateString: string) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  date.setMinutes(date.getMinutes() + date.getTimezoneOffset());
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const TrainingPlanDisplay: React.FC<TrainingPlanDisplayProps> = ({ plan, formData, onReset }) => {
  if (!plan || !plan.weekly_plan) {
    return (
      <Box>
        <Typography color="error">The generated plan is missing the 'weekly_plan' structure.</Typography>
        <pre>{JSON.stringify(plan, null, 2)}</pre>
        <Button onClick={onReset}>Try Again</Button>
      </Box>
    );
  }

  const handlePrint = () => {
    window.print();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }} className="no-print">
        <Box>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold' }}>Your Personal Training Plan</Typography>
          <Typography>
            For your <Typography component="span" sx={{ fontWeight: 'bold' }}>{formData.distance}</Typography> with a goal of <Typography component="span" sx={{ fontWeight: 'bold' }}>{formData.goalTime}</Typography>.
          </Typography>
          <Typography>Start Date: <Typography component="span" sx={{ fontWeight: 'bold' }}>{formatDate(formData.startDate)}</Typography></Typography>
          <Typography>Race Day: <Typography component="span" sx={{ fontWeight: 'bold' }}>{formatDate(formData.raceDate)}</Typography></Typography>
        </Box>
        <Box>
          <Button variant="outlined" onClick={onReset} sx={{ mr: 1 }}>Create New Plan</Button>
          <Button variant="contained" onClick={handlePrint}>Print Plan</Button>
        </Box>
      </Box>

      <Box className="no-print">
        <Grid container spacing={3}>
          {plan.weekly_plan.map((week) => (
            <Grid item xs={12} key={week.week_number}>
              <Paper elevation={2} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, mb: 2 }}>
                  <Typography variant="h5" component="h3" sx={{ fontWeight: 'bold' }}>Week {week.week_number}</Typography>
                </Box>
                <Grid container spacing={2}>
                  {week.days && week.days.map((workout) => (
                    <Grid item xs={12} sm={6} md={3} lg={12/7} key={workout.day_of_week}>
                      <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                        <Typography sx={{ fontWeight: 'bold' }}>{workout.day_of_week}</Typography>
                        <Typography variant="caption" color="text.secondary">{formatDate(workout.calendar_date)}</Typography>
                        <Box sx={{ mt: 1 }}>
                          <Typography component="span" sx={getWorkoutTypeStyles(workout.workout_type || 'Workout')}>
                            {workout.workout_type || 'Workout'}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ mt: 1 }}>{workout.description}</Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box id="print-content">
        <Typography variant="h4" component="h2">Your Training Plan</Typography>
        <table>
          <thead>
            <tr>
              <th>Week</th>
              <th>Day</th>
              <th>Date</th>
              <th>Workout Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {plan.weekly_plan.map((week) =>
              week.days.map((workout, dayIndex) => (
                <tr key={`${week.week_number}-${workout.day_of_week}`}>
                  {dayIndex === 0 && <td rowSpan={week.days.length}>{week.week_number}</td>}
                  <td>{workout.day_of_week}</td>
                  <td>{formatDate(workout.calendar_date)}</td>
                  <td>{workout.workout_type || 'Workout'}</td>
                  <td>{workout.description}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Box>
    </Box>
  );
};
